import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
// `/toast` has no flat `dist/toast.d.ts` — see `index.tsx`'s import comment.
import { addToast } from '@clay-run/glaze/toast/index';
import { addApiErrorToast } from '../../_shared/api-error-toast';
import { runBulkSubmit } from '../bulk-submit';
import { resetSubmitStatusForRetry, seedScanIntoScansCaches, useScanApi } from './useScanApi';
import type { Scan } from '../scan';

/**
 * The impure wiring half of "Submit N ready drafts"
 * (plans/2026-09-18-001-lead-scanner-heic-bulk-submit-plan.md §2,
 * Decision 2) — owns the react-query client, the `submitScan` call, and the
 * one-toast-per-batch summary. `runBulkSubmit` (`../bulk-submit.ts`) owns the
 * sequencing/concurrency and is unit-tested on its own; this hook is React +
 * Glaze and is browser-QA'd instead (no React-DOM harness under
 * `mini-apps/`).
 *
 * Deliberately NOT a `useMutation`: calling the plain `submitScan` wrapper in
 * a loop bypasses react-query's global error toast (`mini-apps/CLAUDE.md`'s
 * `meta.suppressErrorToast` contract) the same way `ScanReview`'s own submit
 * mutation opts out of it — a failed row here is reported once, in the
 * batch's own summary toast, never per-row.
 */

export interface BulkSubmitProgress {
  inFlight: boolean;
  total: number;
  resolved: number;
}

export interface UseBulkSubmitResult extends BulkSubmitProgress {
  /** Fires the batch. Guarded against re-entry — a call while `inFlight` is
   *  a no-op, same stance as `PhotoIntake.tsx`'s `isIntaking` guard. */
  run: (scans: Scan[]) => Promise<void>;
}

const INITIAL_PROGRESS: BulkSubmitProgress = { inFlight: false, total: 0, resolved: 0 };

export function useBulkSubmit(): UseBulkSubmitResult {
  const scanApi = useScanApi();
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<BulkSubmitProgress>(INITIAL_PROGRESS);
  // `useState` alone would read the CLOSED-OVER `progress.inFlight` from the
  // render that created this `run`, which is stale the instant a second
  // click races the first before a re-render lands. A ref is the guard that
  // actually reflects "is a batch running right now".
  const inFlightRef = useRef(false);

  const run = useCallback(
    async (scans: Scan[]) => {
      if (inFlightRef.current || scans.length === 0) return;
      inFlightRef.current = true;
      setProgress({ inFlight: true, total: scans.length, resolved: 0 });

      try {
        const { submitted, failed } = await runBulkSubmit(scans, {
          submitScan: scanApi.submitScan,
          onScanSettled: (outcome) => {
            if (outcome.ok) {
              // Same reasoning as `ScanReview.tsx`'s `submitMutation.onSuccess`:
              // seed the freshly-claimed `submitting` row straight into every
              // cached scans list rather than waiting on that list's own poll,
              // and drop any stale submit-status cache entry so a later open
              // of this scan's review screen polls the run this call just
              // fired, not a leftover from a previous submission.
              seedScanIntoScansCaches(queryClient, outcome.response.scan);
              resetSubmitStatusForRetry(queryClient, outcome.scanId);
            }
            setProgress((prev) => ({ ...prev, resolved: prev.resolved + 1 }));
          },
        });

        // One summary toast for the whole batch (Decision 5 of the plan) —
        // never one per row. A failed row is left showing its own error
        // state in the list; this toast just points the rep at it.
        if (submitted.length > 0) {
          addToast({
            content: `Submitted ${submitted.length} draft${submitted.length === 1 ? '' : 's'}`,
            status: 'success',
          });
        }
        if (failed.length > 0) {
          addApiErrorToast({
            content: `Couldn't submit ${failed.length} of ${scans.length} drafts — open each one to see why.`,
            error: failed[0].error,
          });
        }
      } finally {
        inFlightRef.current = false;
        setProgress(INITIAL_PROGRESS);
      }
    },
    [scanApi, queryClient],
  );

  return { ...progress, run };
}
